import { Box, useMediaQuery, useTheme } from '@mui/material';

import type { Doc_Template_Type_Enum_Enum } from '@graphql/__generated__/graphql';
import { LoadingSpinner } from '@modules/_shared/components/LoadingSpinner';
import DesktopLayout from '@modules/_shared/components/report-editor/layout/DesktopLayout';
import MobileLayout from '@modules/_shared/components/report-editor/layout/MobileLayout';
import DesktopPreview from '@modules/_shared/components/report-editor/preview/DesktopPreview';
import ReportEditorProvider, {
  type PageId,
  type ReportData,
  type StepType,
} from '@modules/_shared/components/report-editor/ReportEditorContext';
import type { Language } from '@shared/LocaleContext';

type ReportEditorProps<T extends PageId> = {
  isLoading?: boolean;
  steps: StepType<T>[];
  data: ReportData;
  documentId: string;
  templateId: string;
  documentType: Doc_Template_Type_Enum_Enum;
  language?: Language;
  exitPath: string;
  includeFields: string[];
  previewOnly?: boolean;
  readOnly?: boolean;
  previewVersion?: string;
};

const ReportEditor = <T extends PageId>({
  isLoading = false,
  steps,
  data,
  documentId,
  templateId,
  documentType,
  language,
  exitPath,
  includeFields,
  previewOnly = false,
  readOnly = false,
  previewVersion,
}: ReportEditorProps<T>) => {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const renderLayout = () => {
    if (previewOnly) {
      return <DesktopPreview />;
    }
    if (isTablet) {
      return <MobileLayout />;
    }
    return <DesktopLayout />;
  };

  return (
    <ReportEditorProvider<T>
      data={data}
      steps={steps}
      documentId={documentId}
      templateId={templateId}
      documentType={documentType}
      language={language}
      exitPath={exitPath}
      includeFields={includeFields}
      previewOnly={previewOnly}
      readOnly={readOnly}
      previewVersion={previewVersion}
    >
      <Box
        sx={{
          height: isTablet && !previewOnly ? 'calc(100dvh - 114px)' : '100%',
        }}
      >
        {renderLayout()}
      </Box>
    </ReportEditorProvider>
  );
};

export default ReportEditor;
